import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useTranslation } from '../i18n/useTranslation';
import { updateAccount, deleteCarpoolOffer, API_BASE_URL as API_URL } from '../services/api';
import { HeaderBar } from '../components/ui/HeaderBar';
import { motion } from 'framer-motion';
import './MyAccount.css';

// Skeleton Loading Component
const SkeletonCard = () => (
  <div className="skeleton-card">
    <div className="skeleton-header">
      <div className="skeleton-line title"></div>
      <div className="skeleton-line badge"></div>
    </div>
    <div className="skeleton-line text"></div>
    <div className="skeleton-line text"></div>
    <div className="skeleton-line text"></div>
  </div>
);

const SkeletonLoader = () => (
  <div className="loading-state">
    <SkeletonCard />
    <SkeletonCard />
    <SkeletonCard />
  </div>
);

function MyAccount() {
  const navigate = useNavigate();
  const { authData, setAuthData, isAuthenticated, authLoading, logout, showToast } = useApp();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [offers, setOffers] = useState([]);
  const [rides, setRides] = useState([]); // Rides as passenger (confirmed)
  const [requests, setRequests] = useState([]); // Join requests sent (all statuses)
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '' });

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'confirmed', 'pending', 'rejected'
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'upcoming', 'past'

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { returnTo: '/my-account' } });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Load user data
  const loadUserData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');

      // Fetch all data in parallel
      // Note: my-join-requests returns ALL join requests (all statuses), so we use only that
      // my-joined-rides also returns the same data, so we skip it to avoid duplicates
      const [eventsRes, offersRes, requestsRes] = await Promise.all([
        fetch(`${API_URL}/auth/my-events`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.json()).catch(() => ({ events: [] })),
        fetch(`${API_URL}/auth/my-offers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.json()).catch(() => ({ offers: [] })),
        fetch(`${API_URL}/auth/my-join-requests`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.json()).catch(() => ({ requests: [] }))
      ]);

      setEvents(eventsRes.events || []);
      setOffers(offersRes.offers || []);
      // Split join requests by status: confirmed = rides, others = requests
      const allJoinRequests = requestsRes.requests || [];
      setRides(allJoinRequests.filter(r => r.status === 'confirmed'));
      setRequests(allJoinRequests.filter(r => r.status !== 'confirmed'));
    } catch (error) {
      showToast(t('myAccount.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove dependencies to prevent re-creation

  // Load data only once when authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadUserData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading]); // Only depend on auth state, not loadUserData

  // Set profile form separately - only when authData first becomes available
  useEffect(() => {
    if (authData?.name || authData?.email) {
      setProfileForm(prev => {
        // Only update if different to avoid unnecessary re-renders
        if (prev.name !== (authData?.name || '') || prev.email !== (authData?.email || '')) {
          return {
        name: authData?.name || '',
        email: authData?.email || ''
          };
        }
        return prev;
      });
    }
  }, [authData?.name, authData?.email]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const response = await updateAccount(profileForm);
      setAuthData(response.account);
      setEditingProfile(false);
      showToast(t('myAccount.profileUpdated'), 'success');
    } catch (error) {
      showToast(error.message || t('myAccount.failedToUpdate'), 'error');
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm(t('myAccount.confirmDeleteOffer'))) return;

    try {
      await deleteCarpoolOffer(offerId);
      setOffers(offers.filter(o => o.offer_id !== offerId));
      showToast(t('myAccount.offerDeleted'), 'success');
    } catch (error) {
      showToast(t('myAccount.failedToDeleteOffer'), 'error');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm(t('myAccount.confirmCancelRequest'))) return;

    try {
      const token = localStorage.getItem('authToken');
      // Use join-request endpoint for join requests (both pending and confirmed)
      const response = await fetch(`${API_URL}/carpool/join-request/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        showToast(t('myAccount.requestCancelled'), 'success');
        await loadUserData(); // Reload all data
      } else {
        showToast(t('myAccount.failedToCancelRequest'), 'error');
      }
    } catch (error) {
      showToast(t('myAccount.failedToCancelRequest'), 'error');
    }
  };

  const handleDeleteEvent = async (eventCode, eventName) => {
    if (!window.confirm(t('myAccount.confirmDeleteEvent').replace('{eventName}', eventName))) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/event/${eventCode}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setEvents(events.filter(e => e.event_code !== eventCode));
        showToast(t('myAccount.eventDeleted'), 'success');
      } else {
        const data = await response.json();
        showToast(data.message || t('myAccount.failedToDeleteEvent'), 'error');
      }
    } catch (error) {
      showToast(t('myAccount.failedToDeleteEvent'), 'error');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusClass = (status) => {
    const map = {
      confirmed: 'confirmed',
      pending: 'pending',
      rejected: 'rejected',
      active: 'active',
    };
    return map[status] || 'pending';
  };

  if (authLoading) {
    return (
      <div className="my-account-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>{t('myAccount.loadingAccount')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Calculate stats
  const confirmedRides = rides.filter(r => r.status === 'confirmed').length;
  const pendingRequests = requests.filter(r => r.status === 'pending').length;
  const totalSeatsOffered = offers.reduce((sum, o) => sum + (o.total_seats || 0), 0);

  return (
    <div className="my-account-container">
      <HeaderBar />

      {/* Page Header */}
      <motion.div 
        className="account-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="header-content">
          <h1>{t('myAccount.title')}</h1>
          <p className="header-subtitle">{t('myAccount.subtitle')}</p>
        </div>
      </motion.div>

      {/* Profile Section */}
      <motion.div 
        className="profile-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <div className="profile-card">
          <div className="profile-avatar">
            {authData?.name?.charAt(0)?.toUpperCase() || '?'}
          </div>

          {editingProfile ? (
            <form onSubmit={handleUpdateProfile} className="profile-edit-form">
              <div className="form-group">
                <label className="form-label">{t('myAccount.name')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  placeholder={t('myAccount.namePlaceholder')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('myAccount.email')}</label>
                <input
                  type="email"
                  className="form-input"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  placeholder={t('myAccount.emailPlaceholder')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('myAccount.phone')}</label>
                <input
                  type="tel"
                  className="form-input"
                  value={authData?.phone || ''}
                  disabled
                />
                <small className="form-hint">{t('myAccount.phoneCannotChange')}</small>
              </div>
              <div className="profile-actions">
                <button type="submit" className="btn btn-primary">{t('myAccount.saveChanges')}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditingProfile(false)}>{t('common.cancel')}</button>
              </div>
            </form>
          ) : (
            <>
              <div className="profile-info">
                <h2>{authData?.name || 'User'}</h2>
                <div className="profile-details">
                  <div className="profile-detail-item">
                    <span className="icon">📱</span>
                    <span className="value">{authData?.phone}</span>
                  </div>
                  {authData?.email && (
                    <div className="profile-detail-item">
                      <span className="icon">✉️</span>
                      <span className="value">{authData?.email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats Section */}
              <div className="profile-stats">
                <div className="stat-item">
                  <div className="stat-value">{events.length}</div>
                  <div className="stat-label">{t('myAccount.events')}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{offers.length}</div>
                  <div className="stat-label">{t('myAccount.rides')}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{confirmedRides}</div>
                  <div className="stat-label">{t('myAccount.joined')}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{totalSeatsOffered}</div>
                  <div className="stat-label">{t('myAccount.seats')}</div>
                </div>
              </div>

              <div className="profile-actions">
                <button className="btn btn-secondary" onClick={() => setEditingProfile(true)}>
                  ✏️ {t('myAccount.editProfile')}
                </button>
                <button className="btn btn-danger" onClick={handleLogout}>
                  🚪 {t('header.logout')}
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Tabs - 3 Simple Tabs */}
      <div className="account-tabs">
        <button
          className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          📅 {t('myAccount.events')} <span className="tab-count">{events.length}</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'offers' ? 'active' : ''}`}
          onClick={() => setActiveTab('offers')}
        >
          🚗 {t('myAccount.imDriving')} <span className="tab-count">{offers.length}</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'passenger' ? 'active' : ''}`}
          onClick={() => setActiveTab('passenger')}
        >
          🎫 {t('myAccount.imPassenger')} 
          <span className="tab-count">{rides.length + requests.length}</span>
          {pendingRequests > 0 && <span className="tab-count pending-count">{pendingRequests} {t('myAccount.pending')}</span>}
        </button>
      </div>

      {/* Content */}
      <div className="account-content">
        {loading ? (
          <SkeletonLoader />
        ) : activeTab === 'events' ? (
          /* EVENTS TAB */
          <div className="events-list">
            {events.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📅</div>
                <h3>{t('myAccount.noEventsYet')}</h3>
                <p>{t('myAccount.createOrJoinEvent')}</p>
                <button className="btn btn-primary" onClick={() => navigate('/create-event')}>
                  ✨ {t('header.createEvent')}
                </button>
              </div>
            ) : (
              <div className="events-grid">
              {events.map(event => (
                <motion.div 
                  key={event.event_id} 
                  className="visual-event-card"
                  whileHover={{ y: -4, boxShadow: '0 12px 32px rgba(99, 102, 241, 0.15)' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(`/event/${event.event_code}`)}
                >
                  {/* Event Image or Logo Fallback */}
                  <div className={`event-card-visual ${event.event_image ? 'has-image' : 'has-logo'}`}>
                    {event.event_image ? (
                      <img 
                        src={event.event_image} 
                        alt={event.event_name} 
                        className="event-card-img"
                      />
                    ) : (
                      <div className="logo-container">
                        <img 
                          src="/images/trempi-logo.png" 
                          alt="Trempi Logo" 
                          className="event-card-logo"
                        />
                      </div>
                    )}
                    {event.is_creator && (
                      <span className="creator-crown">👑</span>
                    )}
                  </div>

                  {/* Event Info */}
                  <div className="event-card-info">
                    <h3 className="event-card-name">{event.event_name}</h3>
                    <div className="event-card-meta">
                      <span className="meta-item">
                        📅 {event.event_date && new Date(event.event_date).toLocaleDateString('en-GB', {
                          month: 'short', day: 'numeric'
                        })}
                      </span>
                      <span className="meta-item">🕐 {event.event_time}</span>
                    </div>
                    <div className="event-card-location">
                      📍 {event.event_location?.split(',')[0] || 'Location'}
                    </div>
                  </div>

                  {/* Quick Stats & Actions */}
                  <div className="event-card-footer">
                    <div className="event-mini-stats">
                      <span className="mini-stat">🚗 {event.stats?.total_drivers || 0}</span>
                      <span className="mini-stat">🪑 {event.stats?.total_seats || 0}</span>
                    </div>
                    <div className="event-card-actions">
                      <button
                        className="action-icon-btn copy"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(`${window.location.origin}/event/${event.event_code}`);
                          showToast(t('myAccount.linkCopied'), 'success');
                        }}
                        title="Copy link"
                      >
                        📋
                      </button>
                      {event.is_creator && (
                        <>
                          <button
                            className="action-icon-btn manage"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/event/${event.event_code}/admin`);
                            }}
                            title="Manage event"
                          >
                            ⚙️
                          </button>
                          <button
                            className="action-icon-btn delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteEvent(event.event_code, event.event_name);
                            }}
                            title="Delete event"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <button className="btn btn-primary" onClick={() => navigate('/create-event')}>
                ✨ {t('myAccount.createNewEvent')}
              </button>
            </div>
          </div>
        ) : activeTab === 'offers' ? (
          /* OFFERS TAB - rides I'm offering as driver */
          <div className="offers-list">
            {offers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🚗</div>
                <h3>{t('myAccount.noRidesOffered')}</h3>
                <p>{t('myAccount.startDriving')}</p>
              </div>
            ) : (
              offers.map(offer => (
                <div key={offer.offer_id} className="item-card">
                  <div className="item-header">
                    <div className="event-badge">🚗 {offer.event_name || 'Event'}</div>
                    <span className={`status-pill ${getStatusClass(offer.status)}`}>
                      {offer.status || 'Active'}
                    </span>
                  </div>
                  <div className="item-body">
                    {offer.event_date && (
                      <div className="item-detail">
                        <span className="detail-label">📅 {t('myAccount.date')}</span>
                        <span>{new Date(offer.event_date).toLocaleDateString('en-GB', {
                          weekday: 'short', month: 'short', day: 'numeric'
                        })}</span>
                      </div>
                    )}
                    <div className="item-detail">
                      <span className="detail-label">🪑 {t('myAccount.seats')}</span>
                      <span style={{ fontWeight: 600 }}>
                        {offer.available_seats || offer.total_seats} / {offer.total_seats} {t('myAccount.available')}
                      </span>
                    </div>
                    <div className="item-detail">
                      <span className="detail-label">🔄 {t('myAccount.trip')}</span>
                      <span>{offer.trip_type === 'both' ? t('myAccount.roundTrip') : offer.trip_type === 'going' ? t('myAccount.goingOnly') : t('myAccount.returnOnly')}</span>
                    </div>
                    {offer.confirmed_passengers > 0 && (
                      <div className="item-detail">
                        <span className="detail-label">👥 {t('myAccount.passengers')}</span>
                        <span style={{ color: '#059669', fontWeight: 700 }}>
                          {offer.confirmed_passengers} {t('myAccount.confirmed')}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="item-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/event/${offer.event_code}`)}
                    >
                      {t('myAccount.viewEvent')}
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteOffer(offer.offer_id)}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : activeTab === 'passenger' ? (
          /* PASSENGER TAB - All rides as passenger (merged rides + requests) */
          <div className="passenger-list">
            {/* Filters */}
            <div className="filter-bar">
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('myAccount.allStatus')}</option>
                <option value="confirmed">✅ {t('myAccount.confirmed')}</option>
                <option value="pending">⏳ {t('myAccount.pending')}</option>
                <option value="rejected">❌ {t('myAccount.rejected')}</option>
              </select>
              <select 
                value={dateFilter} 
                onChange={(e) => setDateFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('myAccount.allDates')}</option>
                <option value="upcoming">📅 {t('myAccount.upcoming')}</option>
                <option value="past">📆 {t('myAccount.past')}</option>
              </select>
            </div>

            {(() => {
              // Combine rides (confirmed) and requests (pending/rejected)
              // These are already separated by status, so no deduplication needed
              const allPassengerRides = [
                ...rides.map(r => ({
                  ...r,
                  id: r.id || r.join_request_id,
                  type: 'ride',
                  event_name: r.event_name,
                  event_date: r.event_date,
                  event_time: r.event_time,
                  event_code: r.event_code,
                  event_location: r.event_location,
                  driver_name: r.driver_name,
                  driver_phone: r.driver_phone,
                  pickup_location: r.pickup_location,
                  status: r.status || 'confirmed',
                  created_at: r.created_at
                })),
                ...requests.map(r => ({
                  ...r,
                  id: r.id || r.join_request_id,
                  type: 'request',
                  event_name: r.event_name,
                  event_date: r.event_date,
                  event_time: r.event_time,
                  event_code: r.event_code,
                  event_location: r.event_location,
                  status: r.status || 'pending'
                }))
              ];

              // Filter by status
              let filtered = allPassengerRides;
              if (statusFilter !== 'all') {
                filtered = filtered.filter(r => r.status === statusFilter);
              }

              // Filter by date
              if (dateFilter !== 'all') {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                filtered = filtered.filter(r => {
                  if (!r.event_date) return true;
                  const eventDate = new Date(r.event_date);
                  return dateFilter === 'upcoming' ? eventDate >= today : eventDate < today;
                });
              }

              // Sort: confirmed/pending first, then rejected, newest first within each group
              filtered.sort((a, b) => {
                const statusOrder = { confirmed: 0, pending: 1, rejected: 2 };
                const statusDiff = (statusOrder[a.status] || 1) - (statusOrder[b.status] || 1);
                if (statusDiff !== 0) return statusDiff;
                return new Date(b.created_at) - new Date(a.created_at);
              });

              if (filtered.length === 0) {
                return (
                  <div className="empty-state">
                    <div className="empty-icon">🎫</div>
                    <h3>{t('myAccount.noRidesFound')}</h3>
                    <p>{statusFilter !== 'all' || dateFilter !== 'all' 
                      ? t('myAccount.adjustFilters') 
                      : t('myAccount.noRidesAsPassenger')}</p>
                  </div>
                );
              }

              return filtered.map((ride, idx) => (
                <div key={ride.id || idx} className="item-card">
                  <div className="item-header">
                    <div className="event-badge">🚗 {ride.event_name || 'Event'}</div>
                    <span className={`status-pill ${getStatusClass(ride.status)}`}>
                      {ride.status === 'confirmed' ? '✅ ' + t('myAccount.confirmed') :
                       ride.status === 'pending' ? '⏳ ' + t('myAccount.waiting') :
                       ride.status === 'rejected' ? '❌ ' + t('myAccount.rejected') : ride.status}
                    </span>
                  </div>
                  <div className="item-body">
                    {ride.event_date && (
                      <div className="item-detail">
                        <span className="detail-label">📅 {t('myAccount.date')}</span>
                        <span>
                          {new Date(ride.event_date).toLocaleDateString('en-GB', {
                            weekday: 'short', month: 'short', day: 'numeric'
                          })} {ride.event_time && t('myAccount.at') + ' ' + ride.event_time}
                        </span>
                      </div>
                    )}
                    <div className="item-detail">
                      <span className="detail-label">👤 {t('myAccount.driver')}</span>
                      <span style={{ fontWeight: 700 }}>{ride.driver_name || t('myAccount.unknown')}</span>
                    </div>
                    {ride.status === 'confirmed' && ride.driver_phone && (
                      <div className="item-detail">
                        <span className="detail-label">📱 {t('myAccount.contact')}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {ride.driver_phone}
                          <a href={`tel:${ride.driver_phone}`} style={{ fontSize: '18px' }}>📞</a>
                          <a
                            href={`https://wa.me/${ride.driver_phone?.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: '18px' }}
                          >
                            💬
                          </a>
                        </span>
                      </div>
                    )}
                    {ride.status === 'pending' && (
                      <div className="item-detail" style={{ background: '#FEF3C7', padding: '8px 12px', borderRadius: '8px' }}>
                        <span style={{ color: '#92400E', fontSize: '13px' }}>
                          ⏳ {t('myAccount.driverContactPending')}
                        </span>
                      </div>
                    )}
                    {ride.pickup_location && (
                      <div className="item-detail">
                        <span className="detail-label">📍 {t('myAccount.pickup')}</span>
                        <span>{ride.pickup_location}</span>
                      </div>
                    )}
                    {ride.event_location && (
                      <div className="item-detail">
                        <span className="detail-label">🎯 {t('myAccount.destination')}</span>
                        <span>{ride.event_location}</span>
                      </div>
                    )}
                  </div>
                  <div className="item-actions">
                    {ride.event_code && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate(`/event/${ride.event_code}`)}
                      >
                        {t('myAccount.viewEvent')}
                      </button>
                    )}
                    {(ride.status === 'pending' || ride.status === 'confirmed') && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteRequest(ride.id || ride.join_request_id)}
                      >
                        {ride.status === 'pending' ? t('myAccount.cancelRequest') : t('myAccount.cancelRide')}
                      </button>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>
        ) : (
          /* Fallback for old 'rides' or 'requests' tab - redirect to passenger */
          <div className="empty-state">
            <p>This tab has been merged into "I'm a Passenger"</p>
          </div>
        )}
      </div>

      {/* Quick Action FAB */}
      <button
        className="quick-action-fab"
        onClick={() => navigate('/create-event')}
        title="Create new event"
      >
        +
      </button>
    </div>
  );
}

export default MyAccount;



